// FASE 05 — Cadastros Gerais, Ligas e Têmperas (plano de 23/09/2026, Frente B3).
// Mockup de 11/08: o modal de têmpera tem o campo, o botão Cadastrar e a lista das têmperas.
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigation } from "contexts/notificationsContext";
import { excluirCadastro, listarTodos, salvarCadastro } from "services/modules/cadastrosGerais";

interface ITempera {
  idTempera: number;
  descricao: string;
}

interface ModalTemperasProps {
  aberto: boolean;
  onFechar: () => void;
}

export function ModalTemperas({ aberto, onFechar }: ModalTemperasProps) {
  const { openNotification } = useNavigation();
  const [temperas, setTemperas] = useState<ITempera[]>([]);
  const [descricao, setDescricao] = useState("");
  const [emEdicao, setEmEdicao] = useState<ITempera | null>(null);
  const [processando, setProcessando] = useState(false);

  const carregar = async () => {
    try {
      setTemperas(await listarTodos<ITempera>("/Tempera"));
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível carregar as têmperas", type: "error" });
    }
  };

  useEffect(() => {
    if (!aberto) return;
    setDescricao("");
    setEmEdicao(null);
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const salvar = async () => {
    if (!descricao.trim()) return;
    setProcessando(true);
    try {
      await salvarCadastro("/Tempera", { idTempera: emEdicao?.idTempera ?? 0, descricao: descricao.trim() });
      openNotification({ message: "Têmpera salva com sucesso!", type: "success" });
      setDescricao("");
      setEmEdicao(null);
      await carregar();
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível salvar a têmpera", type: "error" });
    } finally {
      setProcessando(false);
    }
  };

  const excluir = async (tempera: ITempera) => {
    setProcessando(true);
    try {
      await excluirCadastro("/Tempera", "idTempera", tempera.idTempera);
      openNotification({ message: "Têmpera excluída com sucesso!", type: "success" });
      await carregar();
    } catch (error: any) {
      openNotification({ message: error?.message || "Não foi possível excluir a têmpera", type: "error" });
    } finally {
      setProcessando(false);
    }
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="sm" fullWidth>
      <DialogTitle>
        Cadastro de têmpera
        <IconButton onClick={onFechar} sx={{ position: "absolute", right: 8, top: 8 }} aria-label="Fechar">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            fullWidth
            label="Têmpera"
            value={descricao}
            onChange={(evento) => setDescricao(evento.target.value)}
            helperText={emEdicao ? `Editando "${emEdicao.descricao}"` : "Obrigatório"}
          />
          <LoadingButton variant="contained" onClick={salvar} loading={processando} disabled={!descricao.trim()}>
            {emEdicao ? "Salvar" : "Cadastrar"}
          </LoadingButton>
        </Stack>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
          Listagem de têmperas do MOF
        </Typography>
        <List dense>
          {temperas.map((tempera) => (
            <ListItem
              key={tempera.idTempera}
              secondaryAction={
                <>
                  <Tooltip title="Editar">
                    <IconButton
                      onClick={() => {
                        setEmEdicao(tempera);
                        setDescricao(tempera.descricao);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton color="error" disabled={processando} onClick={() => excluir(tempera)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <ListItemText primary={tempera.descricao} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
